import React, { useEffect, useState } from "react";
import { FaCalendarAlt, FaListAlt, FaTachometerAlt, FaChevronRight, FaSignOutAlt, FaCog, FaQuestionCircle, FaStore, FaBroadcastTower, FaQrcode } from "react-icons/fa";
import { FaNoteSticky, FaTicket, FaUser, FaKeyboard } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet } from "react-router-dom";
import HandleLogout from "../hooks/handleLogout";
import { setTypingMode } from "../store/activePage";

const NAV_LINKS = [
  { to: "/dashboard/home", menu: "home", icon: FaTachometerAlt, label: "Home" },
  { to: "/dashboard/events-list", menu: "upcomingEvents", icon: FaCalendarAlt, label: "Events List" },
  { to: "/dashboard/live-tracker", menu: "liveTracker", icon: FaBroadcastTower, label: "Live Tracker" },
  { to: "/dashboard/scan-qr", menu: "scanQr", icon: FaQrcode, label: "Scan QR Code" },
  { to: "/dashboard/create-token", menu: "createToken", icon: FaTicket, label: "Create a Token" },
  { to: "/dashboard/token-list", menu: "tokenList", icon: FaListAlt, label: "Token History" },
  { to: "/dashboard/moi-note", menu: "moiNote", icon: FaNoteSticky, label: "Moi Note" },
  // { to: "/dashboard/find-vendors", menu: "vendorSearch", icon: FaStore, label: "Find Enterprise" },
  { to: "/dashboard/settings", menu: "settingsPage", icon: FaCog, label: "Settings" },
];
const TYPING_MODE_OPTIONS = [
  { value: "tanglish", label: "Tanglish -> Tamil" },
  { value: "english", label: "English" },
];
const ACCENT = "#d97706";
const ACCENT_SOFT = "#fcd34d";

const CustomerDashboard = () => {
  const { headerName, activeMenu, typingMode = "tanglish" } = useSelector((state) => state.activePage);
  const [language, setLanguage] = useState("en");
  const [cusName, setCusName] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dispatch = useDispatch();

  const TypingModeSelector = () => (
    <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-0.5">
      {TYPING_MODE_OPTIONS.map((option) => {
        const isActive = typingMode === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => dispatch(setTypingMode(option.value))}
            className={`rounded-lg px-2.5 py-1.5 text-[10px] font-bold transition-all ${isActive
                ? "bg-amber-600 text-white shadow-sm"
                : "text-slate-500 hover:text-slate-800"
              }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  useEffect(() => {
    const cus = localStorage.getItem("customer-name");
    setCusName(cus);
    const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
    setLanguage(match?.[1] || "en");
  }, []);

  const handleLanguageChange = (newLang) => {
    const cookieValue = `/en/${newLang}`;
    const domain = window.location.hostname;
    document.body.classList.add("switching-language");
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
    const hostParts = domain.split(".");
    if (hostParts.length > 2) {
      const rootDomain = hostParts.slice(-2).join(".");
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${rootDomain}`;
    }
    document.cookie = `googtrans=${cookieValue}; path=/`;
    document.cookie = `googtrans=${cookieValue}; path=/; domain=${domain}`;
    setTimeout(() => window.location.reload(), 300);
  };

  const logo = require("../assets/images/logo.jpeg");

  return (
    <>
      <style>{`
        .cus-sidebar-scrollbar::-webkit-scrollbar { width: 4px; }
        .cus-sidebar-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .cus-sidebar-scrollbar::-webkit-scrollbar-thumb { background: rgba(217,119,6,0.25); border-radius: 99px; }
        .cus-nav-link { transition: all 0.2s ease; }
        .cus-nav-link:hover { background: rgba(217,119,6,0.06); }
        .cus-nav-active { background: rgba(217,119,6,0.08); border-left: 3px solid #d97706; }
        .cus-main-scroll::-webkit-scrollbar { width: 6px; }
        .cus-main-scroll::-webkit-scrollbar-track { background: #f8fafc; }
        .cus-main-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
      `}</style>

      <div className="flex h-screen overflow-hidden bg-slate-900">

        {/* ── Sidebar ──────────────────────────────────────────────────────── */}
        <aside
          style={{
            width: collapsed ? "72px" : "260px",
            background:
              "linear-gradient(180deg, #0f172a 0%, #111827 48%, #030712 100%)",
            transition: "width 0.3s cubic-bezier(0.4,0,0.2,1)",
            borderRight: "1px solid rgba(226,232,240,0.08)",
            boxShadow: "4px 0 24px rgba(0,0,0,0.25)",
          }}
          className="hidden md:flex flex-col h-full shrink-0 overflow-hidden"
        >
          {/* Logo area */}
          <div
            style={{ borderBottom: "1px solid rgba(226,232,240,0.08)" }}
            className="flex items-center gap-3 px-4 py-4 shrink-0"
          >
            <div className="relative shrink-0">
              <img src={logo} className="w-10 h-10 rounded-xl object-cover ring-2 ring-amber-500/25" alt="logo" />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 border-2 border-[#0f172a] rounded-full" />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-white font-bold text-sm tracking-wide leading-tight truncate">Digital Moi Tech</p>
                <p className="text-amber-500/90 text-xs font-semibold truncate">Customer Portal</p>
              </div>
            )}
            <button
              onClick={() => setCollapsed((p) => !p)}
              style={{ marginLeft: "auto", color: "rgba(226,232,240,0.35)" }}
              className="shrink-0 hover:text-amber-500 transition-colors p-1"
            >
              <FaChevronRight
                style={{ transform: collapsed ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.3s ease" }}
                className="text-xs"
              />
            </button>
          </div>

          {/* Nav links */}
          <nav className="flex-1 overflow-y-auto cus-sidebar-scrollbar px-2 py-3 space-y-0.5">
            {NAV_LINKS.map(({ to, menu, icon: Icon, label }) => {
              const isActive = activeMenu === menu;
              return (
                <Link
                  key={to}
                  to={to}
                  title={collapsed ? label : undefined}
                  className={`cus-nav-link flex items-center gap-3 px-3 py-3 rounded-xl no-underline ${isActive ? "cus-nav-active" : ""}`}
                >
                  <Icon
                    style={{ color: isActive ? ACCENT : "rgba(226,232,240,0.38)", flexShrink: 0, fontSize: "1rem" }}
                  />
                  {!collapsed && (
                    <span
                      style={{ color: isActive ? ACCENT_SOFT : "rgba(226,232,240,0.7)" }}
                      className="text-sm font-semibold truncate"
                    >
                      {label}
                    </span>
                  )}
                  {!collapsed && isActive && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User footer */}
          <div style={{ borderTop: "1px solid rgba(226,232,240,0.08)" }} className="px-3 py-3 shrink-0 space-y-2">
            {/* User info */}
            <div
              style={{ background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.15)" }}
              className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 overflow-hidden"
            >
              <div className="w-7 h-7 rounded-full bg-amber-500/12 border border-amber-500/25 flex items-center justify-center shrink-0">
                <FaUser className="text-amber-500 text-xs" />
              </div>
              {!collapsed && (
                <span className="text-gray-200 text-xs font-semibold truncate">
                  {cusName || "Customer"}
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-1.5">
              <button
                onClick={() => HandleLogout()}
                title="Logout"
                style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.25)" }}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-red-400 hover:bg-red-500/20 transition text-xs font-bold"
              >
                <FaSignOutAlt />
                {!collapsed && "Logout"}
              </button>
              <Link
                to="/dashboard/settings"
                title="Settings"
                style={{ background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.15)" }}
                className={`flex items-center justify-center w-9 rounded-xl transition ${activeMenu === "settingsPage"
                    ? "text-amber-500"
                    : "text-slate-400 hover:text-amber-500"
                  }`}
              >
                <FaCog className="text-xs" />
              </Link>
            </div>
          </div>
        </aside>

        {/* ── Main content ─────────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top bar */}
          <header
            style={{
              borderBottom: "1px solid #e2e8f0",
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
            }}
            className="flex items-center justify-between px-4 py-3 md:px-6 md:py-3 shrink-0"
          >
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-5 rounded-full bg-[#d97706] shrink-0" />
              <h1 className="text-lg font-bold text-slate-800 tracking-tight">{headerName}</h1>
            </div>

            {/* Language toggle */}
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-1 rounded-full bg-slate-100 p-0.5 border border-slate-200/50">
                {["en", "ta"].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-all ${language === lang
                        ? "bg-white text-slate-800 shadow-sm"
                        : "text-slate-500 hover:text-slate-800"
                      }`}
                  >
                    {lang === "en" ? "EN" : "தமிழ்"}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3 border-l border-slate-200 pl-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500 hidden sm:block">Typing Mode</span>
                  <button
                    onClick={() => setShowHelp(true)}
                    className="text-slate-400 hover:text-amber-600 transition-colors"
                    title="Keyboard Shortcuts Help"
                  >
                    <FaQuestionCircle size={15} />
                  </button>
                </div>
                <TypingModeSelector />
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-y-auto cus-main-scroll bg-[#f8fafc] pb-24 md:pb-0">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Bottom Nav Bar for Mobile */}
      <nav
        style={{
          paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))",
          background: "linear-gradient(0deg, #090d16 0%, #0f172a 100%)",
        }}
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-slate-800/80 flex items-center justify-around px-2 pt-2 shadow-[0_-8px_30px_rgba(0,0,0,0.4)]"
      >
        <Link
          to="/dashboard/home"
          className={`flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition ${activeMenu === "home" ? "text-amber-500" : "text-slate-400"
            }`}
        >
          <FaTachometerAlt className="text-lg" />
          <span className="text-[10px] font-bold">Home</span>
        </Link>

        <Link
          to="/dashboard/events-list"
          className={`flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition ${activeMenu === "upcomingEvents" ? "text-amber-500" : "text-slate-400"
            }`}
        >
          <FaCalendarAlt className="text-lg" />
          <span className="text-[10px] font-bold">Events</span>
        </Link>

        {/* Scan QR Code Center Action Button */}
        <Link
          to="/dashboard/scan-qr"
          className={`flex items-center justify-center -mt-6 w-14 h-14 rounded-full bg-gradient-to-tr ${activeMenu === "scanQr"
              ? "from-amber-500 to-amber-400 text-slate-950 scale-105 border-amber-400"
              : "from-amber-600 to-amber-500 text-white"
            } shadow-lg shadow-amber-600/35 transition active:scale-95 border-4 border-slate-900`}
        >
          <FaQrcode className="text-xl" />
        </Link>

        <Link
          to="/dashboard/moi-note"
          className={`flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition ${activeMenu === "moiNote" ? "text-amber-500" : "text-slate-400"
            }`}
        >
          <FaNoteSticky className="text-lg" />
          <span className="text-[10px] font-bold">Moi Note</span>
        </Link>

        <button
          onClick={() => setMobileMenuOpen(true)}
          className={`flex flex-col items-center gap-1 py-1 px-3 rounded-xl transition ${mobileMenuOpen ? "text-amber-500" : "text-slate-400"
            }`}
        >
          <FaListAlt className="text-lg" />
          <span className="text-[10px] font-bold">More</span>
        </button>
      </nav>

      {/* Mobile Slide-Up "More" Sheet */}
      <div
        className={`md:hidden fixed inset-0 z-50 transition-all duration-300 ${mobileMenuOpen ? "visible pointer-events-auto" : "invisible pointer-events-none"
          }`}
      >
        {/* Backdrop */}
        <div
          onClick={() => setMobileMenuOpen(false)}
          className={`absolute inset-0 bg-slate-950/60 backdrop-blur-sm transition-opacity duration-300 ${mobileMenuOpen ? "opacity-100" : "opacity-0"
            }`}
        />
        {/* Sheet */}
        <div
          className={`absolute bottom-0 left-0 right-0 bg-slate-900 rounded-t-3xl border-t border-slate-800 p-6 transition-transform duration-300 shadow-[0_-20px_50px_rgba(0,0,0,0.3)] ${mobileMenuOpen ? "translate-y-0" : "translate-y-full"
            }`}
          style={{ maxHeight: "80vh", overflowY: "auto", paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))" }}
        >
          {/* Grabber line */}
          <div className="w-12 h-1 rounded-full bg-slate-700 mx-auto mb-6" />

          {/* User info */}
          <div className="flex items-center gap-3 bg-slate-800/40 border border-slate-800 rounded-2xl p-4 mb-6">
            <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <FaUser className="text-amber-500 text-base" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Signed In As</p>
              <p className="text-sm font-black text-white truncate">{cusName || "Customer"}</p>
            </div>
          </div>

          {/* Navigation Links Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {[
              { to: "/dashboard/create-token", menu: "createToken", icon: FaTicket, label: "Create a Token" },
              { to: "/dashboard/token-list", menu: "tokenList", icon: FaListAlt, label: "Token History" },
              { to: "/dashboard/live-tracker", menu: "liveTracker", icon: FaBroadcastTower, label: "Live Tracker" },
              { to: "/dashboard/settings", menu: "settingsPage", icon: FaCog, label: "Settings", fullWidth: true },
            ].map(({ to, menu, icon: Icon, label, fullWidth }) => {
              const isActive = activeMenu === menu;
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 p-4 rounded-2xl border text-center transition ${fullWidth
                      ? "col-span-2 flex-row justify-center py-3.5"
                      : "flex-col justify-center"
                    } ${isActive
                      ? "bg-amber-600/10 border-amber-500/30 text-amber-400"
                      : "bg-slate-800/20 border-slate-800/60 text-slate-300 hover:bg-slate-850"
                    }`}
                >
                  <Icon className={`text-xl ${isActive ? "text-amber-500" : "text-slate-400"}`} />
                  <span className="text-xs font-bold">{label}</span>
                </Link>
              );
            })}
          </div>

          {/* Settings Section */}
          <div className="space-y-4 border-t border-slate-800/60 pt-5 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400">Language / மொழி</span>
              <div className="flex items-center gap-1 rounded-full bg-slate-800 p-0.5 border border-slate-700/50">
                {["en", "ta"].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => {
                      handleLanguageChange(lang);
                      setMobileMenuOpen(false);
                    }}
                    className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${language === lang
                        ? "bg-amber-600 text-white shadow-sm"
                        : "text-slate-400 hover:text-slate-200"
                      }`}
                  >
                    {lang === "en" ? "English" : "தமிழ்"}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400">Typing Mode</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowHelp(true);
                    setMobileMenuOpen(false);
                  }}
                  className="text-slate-500 hover:text-amber-500 transition-colors"
                >
                  <FaQuestionCircle size={16} />
                </button>
                <TypingModeSelector />
              </div>
            </div>
          </div>

          {/* Logout Action */}
          <button
            onClick={() => {
              setMobileMenuOpen(false);
              HandleLogout();
            }}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition text-sm font-bold shadow-sm"
          >
            <FaSignOutAlt />
            Logout
          </button>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/20 backdrop-blur-sm p-4 text-left">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-800 tracking-tight">Typing Guide</h3>
              <button onClick={() => setShowHelp(false)} className="text-gray-400 hover:text-gray-600 transition-colors text-2xl">×</button>
            </div>
            <div className="p-6">
              <div className="bg-amber-50/50 border border-amber-100/60 rounded-xl p-4 mb-5 flex gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100/60 flex items-center justify-center shrink-0">
                  <FaKeyboard className="text-amber-700 text-xl" />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-900 mb-1">How it works</p>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    Use <b>Tanglish -&gt; Tamil</b> to type words like <b>vanakkam</b> and convert them on <b>Space</b> or <b>Enter</b>.
                    Use <b>English</b> to keep plain English.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Tanglish Shortcuts</p>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { key: "Alt + J", desc: "Reopen suggestions later for the current translated word" },
                    { key: "Alt + X", desc: "Cycle variants (ந → ண → ன)" },
                    { key: "Alt + 1", desc: "1st option (ந / ல / ர)" },
                    { key: "Alt + 2", desc: "2nd option (ண / ள / ற)" },
                    { key: "Alt + 3", desc: "3rd option (ன / ழ)" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200 group hover:border-amber-300 hover:bg-amber-50/30 transition-all">
                      <span className="text-sm font-semibold text-gray-700">{item.desc}</span>
                      <kbd className="px-2.5 py-1 rounded-lg bg-white border border-gray-300 text-amber-700 text-xs font-black shadow-sm group-hover:border-amber-400 transition-colors">
                        {item.key}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => setShowHelp(false)}
                  className="w-full sm:w-auto px-8 py-3 bg-amber-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-amber-600/20 hover:bg-amber-700 transition-all active:scale-95"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerDashboard;
